import React, { useRef } from "react";
import {
  ArchiveIcon, CalendarDaysIcon, ChartColumnIcon, GripIcon, LayoutGridIcon, LibraryIcon, SparklesIcon,
} from "@/shared/components/icons/animated";

import type { AnimatedIconHandle, AnimatedIconProps } from "@/shared/components/icons/animated";
import type { View } from "@/shared/types";

interface WorkspaceLinkItem {
  key: View;
  label: string;
  Icon: React.ForwardRefExoticComponent<AnimatedIconProps & React.RefAttributes<AnimatedIconHandle>>;
}

/* Pages about the whole workspace rather than one agent. They are listed in the
   sidebar under the logo row and open in a full-height card, with no tab strip. */
const WORKSPACE_NAV: WorkspaceLinkItem[] = [
  { key: "overview", label: "Overview", Icon: ChartColumnIcon },
  { key: "schedule", label: "Schedule", Icon: CalendarDaysIcon },
  { key: "templates", label: "Templates", Icon: LayoutGridIcon },
  { key: "apps", label: "Apps", Icon: GripIcon },
  { key: "knowledge", label: "Knowledge", Icon: LibraryIcon },
  { key: "skills", label: "Skills", Icon: SparklesIcon },
  { key: "archive", label: "Archive", Icon: ArchiveIcon },
];
/* Settings is a workspace page too — full height, no tab strip — but has no row:
   it opens from the profile pill, Upgrade and "Add connection". */
export const WORKSPACE_VIEWS: View[] = [...WORKSPACE_NAV.map((i) => i.key), "workspace-settings"];

function WorkspaceLink({ item, active, rail, onClick }: {
  item: WorkspaceLinkItem;
  active: boolean;
  /** The collapsed sidebar: an icon button with the label as its tooltip. */
  rail?: boolean;
  onClick: () => void;
}) {
  const ref = useRef<AnimatedIconHandle>(null);
  const { Icon } = item;
  const state = active
    ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
    : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => ref.current?.startAnimation()}
      onMouseLeave={() => ref.current?.stopAnimation()}
      title={rail ? item.label : undefined}
      className={
        rail
          ? `press w-9 h-9 rounded-lg flex items-center justify-center ${state}`
          : `press w-full h-[30px] flex items-center gap-2 px-2 rounded-[8px] text-left text-[13.5px] ${state}`
      }
    >
      <Icon ref={ref} size={16} className="shrink-0" />
      {!rail && <span className="truncate flex-1">{item.label}</span>}
    </button>
  );
}

export function WorkspaceNav({ view, onNav, rail }: {
  view: View;
  onNav: (view: View) => void;
  rail?: boolean;
}) {
  return WORKSPACE_NAV.map((item) => (
    <WorkspaceLink
      key={item.key}
      item={item}
      active={item.key === view}
      rail={rail}
      onClick={() => onNav(item.key)}
    />
  ));
}
